import { useEffect } from 'react';
import { useAuthStore } from '../../stores/auth.js';
import { isImpersonationTab } from '../../lib/impersonation-bootstrap.js';
import { postPresenceHeartbeat } from './api.js';

/**
 * Comfortably inside svc-monitor's ONLINE_TTL_SECONDS (300s default), so a
 * single dropped request does not make an active player blink offline, and far
 * under the gateway's per-user ceiling (1200/min).
 */
export const PRESENCE_HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * Reports this session's presence while the tab is authed and visible.
 *
 * Hidden tabs stop reporting rather than keeping a closed laptop "online" for
 * the anti-fraud views; going visible again beats immediately because the TTL
 * may have lapsed in the meantime. Failures are swallowed — presence is
 * telemetry, and a monitor outage must never surface in the lobby.
 *
 * Impersonation tabs never report. Their token is read-only at the gateway
 * (AUTH-IMP-READONLY), so the POST could only ever 403, and a staff "view as
 * member" session is not the member being online.
 */
export function usePresenceHeartbeat(): void {
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));

  useEffect(() => {
    if (!isAuthed || isImpersonationTab()) return;

    let timer: ReturnType<typeof setInterval> | undefined;

    const beat = () => {
      void postPresenceHeartbeat().catch(() => {});
    };

    const start = () => {
      if (timer) return;
      beat();
      timer = setInterval(beat, PRESENCE_HEARTBEAT_INTERVAL_MS);
    };

    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = undefined;
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    if (document.visibilityState !== 'hidden') start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [isAuthed]);
}
